import {
  Asset,
  ProductDataVariant
} from '../../../services/product/product-data'
import { getAssetImageUrl } from '../../../utils/asset-url'
import { getOptimizedImageExtension } from '../../../utils/image-extensions'
import {
  DEFAULT_IMAGE_WIDTH,
  IMAGE_GALLERY_BREAKPOINTS
} from '../../../utils/constants'

type ProductImageGalleryItem = {
  width: string
  src?: string
  renditions?: boolean
  srcSetData?: string
  srcThumbs?: string
  srcFullScreen?: string
  lazyLoading?: boolean
}

type ProductImageGalleryArgs = {
  productVariant?: ProductDataVariant
  backupImageUrl?: string
  enableLazyLoad?: boolean
}

const buildGalleryItem = (
  asset: Asset,
  index: number,
  enableLazyLoad: boolean
): ProductImageGalleryItem | undefined => {
  const assetUrl = getAssetImageUrl(asset)

  if (!assetUrl) {
    return undefined
  }

  const optimizedUrl = getOptimizedImageExtension(assetUrl)

  const srcSetData = IMAGE_GALLERY_BREAKPOINTS.map(
    ({ width, minWidth }) => `${optimizedUrl}?width=${width} ${minWidth}w`
  ).join(', ')

  const srcSetDataWithDefault = `${srcSetData}, ${optimizedUrl}?width=${DEFAULT_IMAGE_WIDTH}`

  return {
    src: optimizedUrl,
    width: '100%',
    renditions: true,
    srcSetData: srcSetDataWithDefault,
    srcThumbs: `${optimizedUrl}?width=64`,
    srcFullScreen: `${optimizedUrl}?width=${DEFAULT_IMAGE_WIDTH}`,
    lazyLoading: index === 0 ? enableLazyLoad : true
  }
}

const getFallbackGalleryItem = (
  backupImageUrl?: string
): ProductImageGalleryItem => ({
  width: '100%',
  ...(backupImageUrl ? { src: backupImageUrl } : {})
})

export const getProductImageGalleryProps = ({
  productVariant,
  backupImageUrl,
  enableLazyLoad = false
}: ProductImageGalleryArgs): ProductImageGalleryItem[] => {
  const assets = productVariant?.assets ?? []

  const galleryItems = assets
    .map((asset, index) => buildGalleryItem(asset, index, enableLazyLoad))
    .filter((item): item is ProductImageGalleryItem => Boolean(item))

  if (galleryItems.length > 0) {
    return galleryItems
  }

  return [getFallbackGalleryItem(backupImageUrl)]
}
